import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

type HEADERS = { [key: string]: string };
type PROBLEM_CODE =
    | 'CLIENT_ERROR'
    | 'SERVER_ERROR'
    | 'TIMEOUT_ERROR'
    | 'CONNECTION_ERROR'
    | 'NETWORK_ERROR'
    | 'UNKNOWN_ERROR'
    | 'CANCEL_ERROR';

interface ApiErrorResponse<T> {
    ok: false;
    problem: PROBLEM_CODE;
    originalError: AxiosError;

    data?: T;
    status?: number;
    headers?: HEADERS;
    config?: AxiosRequestConfig;
    duration?: number;
}
interface ApiOkResponse<T> {
    ok: true;
    problem: null;
    originalError: null;

    data?: T;
    status?: number;
    headers?: HEADERS;
    config?: AxiosRequestConfig;
    duration?: number;
}

type ApiResponse<T, U = T> = ApiErrorResponse<U> | ApiOkResponse<T>;
interface ApiInstanceProp {
    baseURL: string;
}

interface ApiInstance {
    axiosInstance: AxiosInstance;
    get: <T, U = T>(url: string, params?: {}, axiosConfig?: AxiosRequestConfig) => Promise<ApiResponse<T, U>>;
    delete: <T, U = T>(url: string, params?: {}, axiosConfig?: AxiosRequestConfig) => Promise<ApiResponse<T, U>>;
    post: <T, U = T>(url: string, data?: any, axiosConfig?: AxiosRequestConfig) => Promise<ApiResponse<T, U>>;
    put: <T, U = T>(url: string, data?: any, axiosConfig?: AxiosRequestConfig) => Promise<ApiResponse<T, U>>;
}

const NONE = null;
const CLIENT_ERROR = 'CLIENT_ERROR';
const SERVER_ERROR = 'SERVER_ERROR';
const TIMEOUT_ERROR = 'TIMEOUT_ERROR';
const CONNECTION_ERROR = 'CONNECTION_ERROR';
const NETWORK_ERROR = 'NETWORK_ERROR';
const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
const CANCEL_ERROR = 'CANCEL_ERROR';

const TIMEOUT_ERROR_CODES = ['ECONNABORTED'];
const NODEJS_CONNECTION_ERROR_CODES = ['ENOTFOUND', 'ECONNREFUSED', 'ECONNRESET'];
const STATUS_ERROR_CODES = ['ERR_BAD_REQUEST', 'ERR_BAD_RESPONSE'];

const isWithin = (min: number, max: number, value: number): boolean => value >= min && value <= max;
const in200s = (n: number | null): boolean => n != null && isWithin(200, 299, n);
const in400s = (n: number | null): boolean => n != null && isWithin(400, 499, n);
const in500s = (n: number | null): boolean => n != null && isWithin(500, 599, n);

const isPromise = (obj: any) =>
    !!obj && (typeof obj === 'object' || typeof obj === 'function') && typeof obj.then === 'function';

const getProblemFromStatus = (status: number | null) => {
    if (!status) return UNKNOWN_ERROR;
    if (in200s(status)) return NONE;
    if (in400s(status)) return CLIENT_ERROR;
    if (in500s(status)) return SERVER_ERROR;
    return UNKNOWN_ERROR;
};

const getProblemFromError = (error: any) => {
    // first check if the error message is Network Error (set by axios at 0.12) on platforms other than NodeJS.
    if (error.message === 'Network Error') return NETWORK_ERROR;
    if (axios.isCancel(error)) return CANCEL_ERROR;

    // then check the specific error code
    if (!error.code) return getProblemFromStatus(error.response ? error.response.status : null);
    if (STATUS_ERROR_CODES.includes(error.code)) return getProblemFromStatus(error.response.status);
    if (TIMEOUT_ERROR_CODES.includes(error.code)) return TIMEOUT_ERROR;
    if (NODEJS_CONNECTION_ERROR_CODES.includes(error.code)) return CONNECTION_ERROR;
    return UNKNOWN_ERROR;
};

const toNumber = (value: any): number => {
    // if value is a Date, convert to a number
    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === 'number' || value === null || value === undefined) {
        return value;
    }

    return Number(value);
};

const create = (props: ApiInstanceProp): ApiInstance => {
    let instance = axios.create({
        baseURL: props.baseURL,
    });

    const responseTransforms: any[] = [];
    const asyncResponseTransforms: any[] = [];

    type RequestsWithoutBody = 'get' | 'head' | 'delete' | 'link' | 'unlink';
    type RequestsWithBody = 'post' | 'put' | 'patch';

    const doRequestWithoutBody =
        (method: RequestsWithoutBody) =>
        (url: string, params = {}, axiosConfig = {}) => {
            return doRequest({ ...axiosConfig, url, params, method });
        };

    const doRequestWithBody =
        (method: RequestsWithBody) =>
        (url: string, data: any, axiosConfig = {}) => {
            return doRequest({ ...axiosConfig, url, method, data });
        };

    const doRequest = async (axiosRequestConfig: AxiosRequestConfig) => {
        // after the call, convert the axios response, then execute our monitors
        const startTime = toNumber(new Date());
        const chain = async (response: any) => {
            const ourResponse = await convertResponse(startTime, response);
            return ourResponse as any;
        };

        return instance.request(axiosRequestConfig).then(chain).catch(chain);
    };

    const convertResponse = async (startedAt: number, axiosResult: AxiosResponse | AxiosError) => {
        const end: number = toNumber(new Date());
        const duration: number = end - startedAt;

        // new in Axios 0.13 -- some data could be buried 1 level now
        const isError = axiosResult instanceof Error || axios.isCancel(axiosResult);
        const axiosResponse = axiosResult as AxiosResponse;
        const axiosError = axiosResult as AxiosError;
        const response = isError ? axiosError.response : axiosResponse;
        const status = response?.status ?? null;
        const problem = isError ? getProblemFromError(axiosResult) : getProblemFromStatus(status);
        const originalError = isError ? axiosError : null;
        const ok = in200s(status);
        const config = axiosResult.config || null;
        const headers = response?.headers ?? null;
        let data = response?.data ?? null;

        // give an opportunity for anything to the response transforms to change stuff along the way
        let transformedResponse = {
            duration,
            problem,
            originalError,
            ok,
            status,
            headers,
            config,
            data,
        };
        if (responseTransforms.length > 0) {
            responseTransforms.forEach((transform) => transform(transformedResponse));
        }

        // add the async response transforms
        if (asyncResponseTransforms.length > 0) {
            for (let responseTransform of asyncResponseTransforms) {
                const transform = responseTransform(transformedResponse);
                if (isPromise(transform)) {
                    await transform;
                } else {
                    await transform(transformedResponse);
                }
            }
        }

        return transformedResponse;
    };

    const tempInstance: ApiInstance = {
        axiosInstance: instance,
        get: doRequestWithoutBody('get'),
        delete: doRequestWithoutBody('delete'),
        post: doRequestWithBody('post'),
        put: doRequestWithBody('put'),
    };

    return tempInstance;
};

const api = create({
    baseURL: '/api/',
});

const adminApi = create({
    baseURL: '/api/admin/',
});

const apiBase = create({
    baseURL: '/',
});

export { api, adminApi, apiBase };
